import {Show} from 'platform/foundation';

import {useRef, type ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {useIsGridMaximized} from '../../store/selectors/useIsGridMaximized';
import type {EagGridMaximization} from '../../types/EagGridMaximization';
import {GridMaximizationAvailability} from './GridMaximizationAvailability';
import {GridMaximizationSession} from './GridMaximizationSession';
import {MaximizedGridHeader} from './MaximizedGridHeader';

type EagGridSurfaceProps = {
  maximization?: EagGridMaximization;
  sectionHeader?: ReactNode;
  maximizedSectionHeader?: ReactNode;
  children: ReactNode;
};

export function EagGridSurface(props: EagGridSurfaceProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const isRequested = useIsGridMaximized();
  const isMaximized = props.maximization?.enabled === true && isRequested;

  return (
    <div
      ref={surfaceRef}
      data-eag-grid-maximized={isMaximized}
      role={isMaximized ? 'region' : undefined}
      aria-label={isMaximized ? props.maximization?.title : undefined}
      tabIndex={isMaximized ? -1 : undefined}
      className={
        isMaximized
          ? 'fixed inset-0 flex min-h-0 min-w-0 flex-col bg-white z-[calc(var(--z-modal)-1)]'
          : 'contents'
      }
    >
      <Show when={props.maximization?.enabled === true}>
        <GridMaximizationAvailability surfaceRef={surfaceRef} />
      </Show>
      <Show when={isMaximized}>
        <GridMaximizationSession surfaceRef={surfaceRef} />
        <MaximizedGridHeader title={props.maximization?.title ?? ''} />
      </Show>
      <div className={isMaximized ? 'flex min-h-0 min-w-0 flex-1 flex-col gap-4 p-4' : 'contents'}>
        <div className={isMaximized ? 'shrink-0' : 'contents'}>
          {props.sectionHeader}
          {/* Use effective maximization so disabling the feature also removes this content. */}
          <Show when={isMaximized}>{props.maximizedSectionHeader}</Show>
        </div>
        {/* The same space as gap-4 between the section header and the grid when maximized. */}
        <Show when={!isMaximized && isNotNil(props.sectionHeader)}>
          <div className="h-4" />
        </Show>
        <div className={isMaximized ? 'min-h-0 min-w-0 flex-1 overflow-hidden' : 'contents'}>
          {props.children}
        </div>
      </div>
    </div>
  );
}
